import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, Camera, FileText, Calendar, Shield, 
  Loader2, Upload, Check, AlertCircle, Eye, Lock, KeyRound
} from 'lucide-react';

import { API_URL } from '../config/api';

const TYPE_PIECES = [
  { value: 'CNI', label: 'Carte Nationale d\'Identité' },
  { value: 'PASSEPORT', label: 'Passeport' },
  { value: 'PERMIS', label: 'Permis de conduire' },
  { value: 'AUTRE', label: 'Autre document officiel' }
];

export default function Profil() {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingPiece, setUploadingPiece] = useState(false);
  
  const photoInputRef = useRef(null);
  const pieceInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    photo_profil: null,
    type_piece: '',
    piece_identite: null,
    date_expiration_piece: ''
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    ancien_mot_de_passe: '',
    nouveau_mot_de_passe: '',
    confirmation_mot_de_passe: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    setFormData({
      nom: user.nom || '',
      prenom: user.prenom || '',
      email: user.email || '',
      telephone: user.telephone || '',
      photo_profil: user.photo_profil || null,
      type_piece: user.type_piece || '',
      piece_identite: user.piece_identite || null,
      date_expiration_piece: user.date_expiration_piece || ''
    });
  }, [user, navigate]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }
    
    setUploadingPhoto(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await fetch(`${API_URL}/api/profile/upload-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload');
      }
      
      const result = await response.json();
      setFormData(prev => ({ ...prev, photo_profil: result.photo_profil }));
      
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success('Photo de profil mise à jour');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePieceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'application/pdf', 
                         'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez PDF, image ou document Word.');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 10 Mo');
      return;
    }
    
    if (!formData.type_piece) {
      toast.error('Veuillez d\'abord sélectionner le type de pièce');
      return;
    }
    
    setUploadingPiece(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('type_piece', formData.type_piece);
      if (formData.date_expiration_piece) {
        formDataUpload.append('date_expiration', formData.date_expiration_piece);
      }
      
      const response = await fetch(`${API_URL}/api/users/me/piece-identite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'upload');
      }
      
      const result = await response.json();
      setFormData(prev => ({ 
        ...prev, 
        piece_identite: result.piece_identite || 'uploaded'
      }));
      
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success('Pièce d\'identité téléchargée avec succès');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingPiece(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nom: formData.nom,
          prenom: formData.prenom,
          telephone: formData.telephone
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la sauvegarde');
      }
      
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordData.ancien_mot_de_passe || !passwordData.nouveau_mot_de_passe || !passwordData.confirmation_mot_de_passe) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (passwordData.nouveau_mot_de_passe !== passwordData.confirmation_mot_de_passe) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwordData.nouveau_mot_de_passe.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const response = await fetch(`${API_URL}/api/users/me/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(passwordData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors du changement de mot de passe');
      }
      
      toast.success('Mot de passe modifié avec succès');
      setPasswordData({
        ancien_mot_de_passe: '',
        nouveau_mot_de_passe: '',
        confirmation_mot_de_passe: ''
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="profil-page">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <User className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold font-serif">Mon Profil</h1>
            <p className="text-muted-foreground">Gérez vos informations personnelles</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Photo de profil */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Photo de profil
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              <div className="relative mb-4">
                {formData.photo_profil ? (
                  <img 
                    src={formData.photo_profil} 
                    alt="Photo de profil" 
                    className="w-32 h-32 rounded-full object-cover border-4 border-primary/20"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-muted flex items-center justify-center border-4 border-primary/20">
                    <User className="w-16 h-16 text-muted-foreground" />
                  </div>
                )}
                {uploadingPhoto && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                  </div>
                )}
              </div>
              
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="photo-input"
              />
              
              <Button 
                variant="outline" 
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                data-testid="upload-photo-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Changer la photo
              </Button>
              
              <p className="text-xs text-muted-foreground mt-2 text-center">
                JPG, PNG ou WebP. Max 5 Mo.
              </p>
            </CardContent>
          </Card>

          {/* Informations personnelles */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Informations personnelles
              </CardTitle>
              <CardDescription>
                Vos informations de contact et d'identification
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prenom">Prénom</Label>
                  <Input
                    id="prenom"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    data-testid="profil-prenom-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    data-testid="profil-nom-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  disabled
                  className="bg-muted"
                  data-testid="profil-email-input"
                />
                <p className="text-xs text-muted-foreground">L'email ne peut pas être modifié</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="telephone">Téléphone</Label>
                <Input
                  id="telephone"
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  placeholder="+221 77 000 00 00"
                  data-testid="profil-telephone-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="outline" className="flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  {user.roles?.join(', ') || 'CLIENT'}
                </Badge>
              </div>
              
              <Button 
                onClick={handleSaveProfile} 
                disabled={saving}
                className="w-full md:w-auto"
                data-testid="save-profil-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Enregistrer les modifications
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Pièce d&#39;identité */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Pièce d&#39;identité
            </CardTitle>
            <CardDescription>
              Téléchargez votre pièce d'identité pour valider votre compte. Cette étape est nécessaire pour effectuer des réservations.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="type_piece">Type de pièce *</Label>
                <Select 
                  value={formData.type_piece} 
                  onValueChange={(v) => setFormData({ ...formData, type_piece: v })}
                >
                  <SelectTrigger data-testid="type-piece-select">
                    <SelectValue placeholder="Sélectionner le type" />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_PIECES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="date_expiration">Date d&#39;expiration</Label>
                <Input
                  id="date_expiration"
                  type="date"
                  value={formData.date_expiration_piece}
                  onChange={(e) => setFormData({ ...formData, date_expiration_piece: e.target.value })}
                  data-testid="date-expiration-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Statut</Label>
                <div className="flex items-center gap-2 h-10">
                  {formData.piece_identite || user?.id_card_uploaded ? (
                    <Badge className="bg-green-100 text-green-800">
                      <Check className="w-3 h-3 mr-1" />
                      Pièce téléchargée
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Non fournie
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            <Separator className="my-6" />
            
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-1">
                <h4 className="font-medium mb-1">Télécharger votre pièce d'identité</h4>
                <p className="text-sm text-muted-foreground">
                  Formats acceptés : PDF, JPG, PNG, DOC, DOCX. Taille max : 10 Mo.
                </p>
              </div>
              
              <input
                ref={pieceInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handlePieceUpload}
                className="hidden"
                data-testid="piece-identite-input"
              />
              
              <Button 
                onClick={() => pieceInputRef.current?.click()}
                disabled={uploadingPiece || !formData.type_piece}
                variant={formData.piece_identite ? "outline" : "default"}
                data-testid="upload-piece-btn"
              >
                {uploadingPiece ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {formData.piece_identite ? 'Remplacer le document' : 'Télécharger'}
              </Button>
            </div>
            
            {!formData.type_piece && (
              <p className="text-sm text-amber-600 mt-2">
                Veuillez d&#39;abord sélectionner le type de pièce d'identité.
              </p>
            )}
            
            {(formData.piece_identite || user?.piece_identite) && (
              <div className="mt-4 p-4 bg-muted/50 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-primary" />
                  <div>
                    <p className="font-medium">Document téléchargé</p>
                    <p className="text-sm text-muted-foreground">
                      {TYPE_PIECES.find(t => t.value === (formData.type_piece || user?.type_piece))?.label || 'Pièce d\'identité'}
                      {(formData.date_expiration_piece || user?.date_expiration_piece) && (
                        <> • Expire le {formData.date_expiration_piece || user?.date_expiration_piece}</>
                      )}
                    </p>
                  </div>
                </div>
                {(formData.piece_identite || user?.piece_identite)?.startsWith('data:') && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      const win = window.open();
                      win.document.write(`<img src="${formData.piece_identite || user?.piece_identite}" style="max-width:100%"/>`);
                    }}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Voir
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Changement de mot de passe */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Changer le mot de passe
            </CardTitle>
            <CardDescription>
              Modifiez votre mot de passe pour sécuriser votre compte
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ancien_password">Ancien mot de passe *</Label>
                <Input
                  id="ancien_password"
                  type="password"
                  value={passwordData.ancien_mot_de_passe}
                  onChange={(e) => setPasswordData({ ...passwordData, ancien_mot_de_passe: e.target.value })}
                  placeholder="••••••••"
                  data-testid="ancien-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="nouveau_password">Nouveau mot de passe *</Label>
                <Input
                  id="nouveau_password"
                  type="password"
                  value={passwordData.nouveau_mot_de_passe}
                  onChange={(e) => setPasswordData({ ...passwordData, nouveau_mot_de_passe: e.target.value })}
                  placeholder="••••••••"
                  data-testid="nouveau-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirmer le mot de passe *</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirmation_mot_de_passe}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmation_mot_de_passe: e.target.value })}
                  placeholder="••••••••"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Le mot de passe doit contenir au moins 6 caractères.
              </p>
              <Button 
                onClick={handleChangePassword} 
                disabled={changingPassword || !passwordData.ancien_mot_de_passe || !passwordData.nouveau_mot_de_passe || !passwordData.confirmation_mot_de_passe}
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <KeyRound className="w-4 h-4 mr-2" />
                )}
                Changer le mot de passe
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
